import React, { useRef, useCallback, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3, Link, Quote, Undo } from 'lucide-react';

function ToolbarButton({ onClick, icon: Icon, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="p-1.5 text-gray-500 hover:text-slate-dark hover:bg-gray-100 rounded transition-colors"
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
}

export default function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);

  // Only push external value into the DOM when it differs (avoids caret reset on every keystroke)
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const url = prompt('URL del enlace:');
    if (url) exec('createLink', url);
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden bg-white" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50 flex-wrap">
        <ToolbarButton onClick={() => exec('bold')} icon={Bold} title="Negrita" />
        <ToolbarButton onClick={() => exec('italic')} icon={Italic} title="Cursiva" />
        <ToolbarButton onClick={() => exec('underline')} icon={Underline} title="Subrayado" />
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton onClick={() => exec('formatBlock', 'h2')} icon={Heading2} title="Titulo H2" />
        <ToolbarButton onClick={() => exec('formatBlock', 'h3')} icon={Heading3} title="Titulo H3" />
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton onClick={() => exec('insertUnorderedList')} icon={List} title="Lista" />
        <ToolbarButton onClick={() => exec('insertOrderedList')} icon={ListOrdered} title="Lista numerada" />
        <ToolbarButton onClick={() => exec('formatBlock', 'blockquote')} icon={Quote} title="Cita" />
        <div className="w-px h-5 bg-gray-200 mx-1" />
        <ToolbarButton onClick={insertLink} icon={Link} title="Enlace" />
        <ToolbarButton onClick={() => exec('removeFormat')} icon={Undo} title="Limpiar formato" />
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[200px] max-h-[400px] overflow-y-auto p-4 font-serif text-gray-700 leading-relaxed text-sm focus:outline-none article-content"
        data-testid="rich-text-content"
        suppressContentEditableWarning
      />
    </div>
  );
}
